// React
import React, { ReactNode } from 'react';

// Vendors
import { Icon } from '@equisoft/design-elements-react';
import { useTranslation } from 'react-i18next';

// Src
import { ReactComponent as DatabaseIcon } from '@/assets/icons/database.svg';
import { ReactComponent as DashboardIcon } from '@/assets/icons/dashboard.svg';
import { ReactComponent as FileTextIcon } from '@/assets/icons/file-text.svg';
import { ReactComponent as DollarSignIcon } from '@/assets/icons/dollar-sign.svg';
import { ReactComponent as ClipboardIcon } from '@/assets/icons/clipboard.svg';
import { ReactComponent as CheckCircleIcon } from '@/assets/icons/check-circle.svg';
import { ReactComponent as AnchorIcon } from '@/assets/icons/anchor-icon.svg';
import { ReactComponent as ScreenInstanceIcon } from '@/assets/icons/screenmirroring.svg';
import Config from '@/config';

// Types
type RemoteEntry = {
    url: string;
    scope: string;
};

type PluginConfigContainer = {
    roles: [];
    anchorConfig: {
        navTitle: string;
        navLinks: {
            to: string;
            title: string;
            icon: ReactNode;
        }[];
        routeConfig: {
            path: string;
            element: string;
            remoteConfig?: RemoteEntry;
            domain?: string;
        }[];
    };
};

const usePluginsConfig = (): PluginConfigContainer[] => {
    // Hooks
    const { t } = useTranslation();

    // Variables
    const organisationRemoteEntry: RemoteEntry = {
        scope: 'organisation',
        url: Config.REMOTE_ENTRY_PLUGINS['organisation'] || '',
    };

    const securityManagementRemoteEntry: RemoteEntry = {
        scope: 'security_management',
        url: Config.REMOTE_ENTRY_PLUGINS['security_management'] || '',
    };

    const centralizeManagementRemoteEntry: RemoteEntry = {
        scope: 'centralize_management',
        url: Config.REMOTE_ENTRY_PLUGINS['centralize_management'] || '',
    };

    const organisationPlugin: PluginConfigContainer = {
        roles: [],
        anchorConfig: {
            navTitle: t('app.core.default_layout.organisation'),
            navLinks: [{ to: '/settings', icon: <Icon name="settings" />, title: t('app.core.default_layout.general_settings') }],
            routeConfig: [{ path: '/settings/*', element: './PluginGeneralSettings', remoteConfig: organisationRemoteEntry }],
        },
    };

    const productsPlugin: PluginConfigContainer = {
        roles: [],
        anchorConfig: {
            navTitle: t('app.core.default_layout.products'),
            navLinks: [
                { to: '/product-definition', icon: <ClipboardIcon />, title: t('common.product_definition') },
                { to: '/rates', icon: <DollarSignIcon />, title: t('common.rates') },
            ],
            routeConfig: [],
        },
    };

    const securityManagementPlugin: PluginConfigContainer = {
        roles: [],
        anchorConfig: {
            navTitle: t('common.security_management'),
            navLinks: [
                { to: '/users', icon: <Icon name="users" />, title: t('common.users') },
                { to: '/division', icon: <Icon name="organization" />, title: t('common.division') },
                { to: '/roles-and-permissions', icon: <CheckCircleIcon />, title: t('common.roles_permissions') },
            ],
            routeConfig: [
                { path: '/roles-and-permissions/*', element: './PluginRoleAndPermission', remoteConfig: securityManagementRemoteEntry },
                { path: '/users/*', element: './PluginUser', remoteConfig: securityManagementRemoteEntry },
                { path: '/division/*', element: './PluginDivision', remoteConfig: securityManagementRemoteEntry },
            ],
        },
    };

    const centralizeManagementPlugin: PluginConfigContainer = {
        roles: [],
        anchorConfig: {
            navTitle: t('common.centralize_management'),
            navLinks: [
                { to: '/rule', icon: <FileTextIcon />, title: t('common.rules') },
                { to: '/dashboard-tile', icon: <DashboardIcon />, title: t('common.dashboard_tile') },
                { to: '/dynamic-grid', icon: <DatabaseIcon />, title: t('common.dynamic_grid') },
                { to: '/alerts', icon: <Icon name="alertOctagon" />, title: t('common.alerts') },
                { to: '/task-template', icon: <ClipboardIcon />, title: t('common.task_template') },
                { to: '/user-group', icon: <Icon name="users" />, title: t('common.user_groups') },
                { to: '/configuration/anchor-object', icon: <AnchorIcon />, title: t('common.anchor') },
                { to: '/screentemplate/screen-instance', icon: <ScreenInstanceIcon />, title: t('common.screen_instance') },
            ],
            routeConfig: [
                {
                    path: '/configuration/*',
                    element: './PluginCentralizeManagement',
                    remoteConfig: centralizeManagementRemoteEntry,
                    domain: 'configuration',
                },
                {
                    path: '/screentemplate/*',
                    element: './PluginCentralizeManagement',
                    remoteConfig: centralizeManagementRemoteEntry,
                    domain: 'screentemplate',
                },
                { path: '/dashboard-tile/*', element: './PluginDashboardTile', remoteConfig: centralizeManagementRemoteEntry },
                { path: '/dynamic-grid/*', element: './PluginDynamicGrid', remoteConfig: centralizeManagementRemoteEntry },
                { path: '/rule/*', element: './PluginRule', remoteConfig: centralizeManagementRemoteEntry },
                { path: '/task-template/*', element: './PluginTaskTemplate', remoteConfig: centralizeManagementRemoteEntry },
                { path: '/user-group/*', element: './PluginUserGroup', remoteConfig: centralizeManagementRemoteEntry },
            ],
        },
    };

    return [organisationPlugin, productsPlugin, securityManagementPlugin, centralizeManagementPlugin];
};

export { usePluginsConfig };
